import React, { memo, useEffect } from 'react';
import {
  Handle, Position, useStore, useUpdateNodeInternals,
} from '@xyflow/react';
import { ReactComponent as EarthIcon } from 'assets/icons/earth.svg';
import { clsx } from 'clsx';

import { VALUE_CHAIN_MAP_CUSTOM_NODES, VALUE_CHAIN_UNIT_TYPES } from '../../../../../../constants/interfaces';
import {
  getUnitBusinessUnitType, getUnitDisplayName, getUnitEstimated,
} from '../../../../../../utils/reactFlowHelpers';
import {
  hasAIMetadata,
} from '../utils/customUnitNodeHelpers';
import areCustomUnitNodePropsEqual from '../utils/customUnitNodeMemo';
import CustomerNode from './CustomerNode';
import CustomUnitContainer from './CustomUnitContainer';
import InputProductNode from './InputProductNode';
import OwnOperationNode from './OwnOperationNode';
import ProfitCentreNode from './ProfitCentreNode';
import SupplierNode from './SupplierNode';
import { useValueChainLocations } from './ValueChainLocationsProvider';

import type { IValueChainRecord } from '../../../../../../store/slices/valueChainMap/interfaces/IValueChainRecord';
import type { ICustomUnitNode } from '../interfaces/ICustomUnitNode';

import styles from '../sass/CustomUnitNode.module.scss';

type TDisplayRecord =
  Partial<IValueChainRecord> & Pick<IValueChainRecord, 'name' | 'profitCenterIds' | 'uniqueIdentifier'>;

function CustomUnitNode({ id, data, parentId }: ICustomUnitNode) {
  const updateNodeInternals = useUpdateNodeInternals();
  const { getCountryName } = useValueChainLocations();
  const {
    childrenCount,
    draft,
    draftServerId,
    showDraftDeleteButton,
    type,
    records,
    totalChildrenHeight,
    depth,
    tempTotalChildrenHeight,
    isCollapsed,
    countryGroupId,
    countryGroupSize,
    isCountryGroupLeader,
    isCountryGroupCollapsed,
    isHiddenByCountryGroup,
    isAIPanelSelectionEnabled,
    isAIPanelSelected,
    isAISessionPreviewSelected,
    isDraftReviewMode,
    onExpandCountryGroupStack,
    onToggleAIPanelCardSelection,
    isScenarioViewMode,
    isScenarioHighlighted,
    showMonitoringRiskBadge,
  } = data;
  const effectiveTotalChildrenHeight = tempTotalChildrenHeight ?? totalChildrenHeight;
  // Rendered width of the parent unit card; the only re-render trigger is the
  // width value itself, so this stays cheap during drags. Used instead of
  // data.depth for sizing because depth can be stale right after re-nesting
  const parentUnitWidth = useStore((store) => {
    if (!parentId) return undefined;
    const parentNode = store.nodeLookup.get(parentId);
    if (!parentNode || parentNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) return undefined;
    return parentNode.measured?.width ?? undefined;
  });
  const isCountryGroupLeaderCollapsed = Boolean(isCountryGroupLeader && isCountryGroupCollapsed);
  const baseCardsInsideCount = childrenCount || 0;
  const cardsInsideCount = isCountryGroupLeaderCollapsed
    ? countryGroupSize || baseCardsInsideCount
    : baseCardsInsideCount;
  const firstRecord = records?.[0];
  const isAIHighlightedUnit = hasAIMetadata(data.aiMetadata)
    || (records?.some((record) => hasAIMetadata(record.aiMetadata)) ?? false);
  const unitBusinessUnitType = getUnitBusinessUnitType(data);
  const unitEstimated = getUnitEstimated(data);

  useEffect(() => {
    updateNodeInternals(id);
  }, [
    id,
    isCollapsed,
    isCountryGroupLeaderCollapsed,
    effectiveTotalChildrenHeight,
    unitBusinessUnitType,
    unitEstimated,
    updateNodeInternals,
  ]);

  const handleDraftDelete = () => {
    const [, rawUnitId = ''] = id.split('unit_');
    const runtimeUnitId = Number(rawUnitId);
    const unitId = draftServerId ?? runtimeUnitId;
    const unitTitle = getUnitDisplayName(data);

    if (!Number.isInteger(unitId)) {
      return;
    }

    data.onDeleteDraftUnit?.({
      id: unitId,
      nodeId: id,
      runtimeId: runtimeUnitId,
      title: unitTitle,
    });
  };

  const renderNodeContent = (
    {
      name, naceSection, addressCountry, profitCenterIds,
      relianceOnFreeNaturalResources, criticalityRating,
      netBookValue, assetCategory, totalRevenueRolling12MPercents,
      keySupplierTier1OfTotal, totalCost,
      wipFinishedGoodsStock,
      uniqueIdentifier,
      virtualProperties,
      totalRevenueRolling12MActual,
    }: TDisplayRecord,
    collapseProps: {
      isCollapsed: boolean,
      cardsInsideCount: number,
    },
  ) => {
    switch (type) {
      case VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET:
        return (
          <OwnOperationNode
            name={name}
            addressCountry={getCountryName(addressCountry || '')}
            profitCenterIds={profitCenterIds}
            assetCategory={assetCategory}
            naceSection={naceSection}
            netBookValue={netBookValue}
            isCollapsed={collapseProps.isCollapsed}
            cardsInsideCount={collapseProps.cardsInsideCount}
            isDraft={Boolean(draft)}
            showDraftDeleteButton={showDraftDeleteButton}
            onDraftDelete={handleDraftDelete}
          />
        );
      case VALUE_CHAIN_UNIT_TYPES.PROFIT_CENTER:
        return (
          <ProfitCentreNode
            name={name}
            uniqueIdentifier={uniqueIdentifier}
            netBookValue={netBookValue}
            totalGrossMarginRolling12MActual={totalRevenueRolling12MActual}
            totalGrossMarginRolling12MPercents={virtualProperties?.totalRevenuePercents || 0}
            totalRevenueRolling12MActual={totalRevenueRolling12MActual}
            totalRevenueRolling12MPercents={virtualProperties?.totalGrossProfitPercents || 0}
            wipFinishedGoodsStock={wipFinishedGoodsStock}
            naceSection={naceSection}
            virtualProperties={virtualProperties}
            addressCountry={getCountryName(addressCountry || '')}
            isCollapsed={collapseProps.isCollapsed}
            cardsInsideCount={collapseProps.cardsInsideCount}
            isDraft={Boolean(draft)}
            showDraftDeleteButton={showDraftDeleteButton}
            onDraftDelete={handleDraftDelete}
          />
        );
      case VALUE_CHAIN_UNIT_TYPES.CUSTOMER:
        return (
          <CustomerNode
            profitCenterIds={profitCenterIds}
            name={name}
            addressCountry={getCountryName(addressCountry || '')}
            totalRevenueRolling12MPercents={totalRevenueRolling12MPercents}
            naceSection={naceSection}
            totalCost={totalCost}
            isCollapsed={collapseProps.isCollapsed}
            cardsInsideCount={collapseProps.cardsInsideCount}
            isDraft={Boolean(draft)}
            showDraftDeleteButton={showDraftDeleteButton}
            onDraftDelete={handleDraftDelete}
          />
        );
      case VALUE_CHAIN_UNIT_TYPES.PRODUCT:
      case VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT:
        return (
          <InputProductNode
            name={name}
            profitCenterIds={profitCenterIds}
            criticalityRating={criticalityRating}
            relianceOnFreeNaturalResources={relianceOnFreeNaturalResources}
            keySupplierTier1OfTotal={keySupplierTier1OfTotal}
            totalCost={totalCost}
            isCollapsed={collapseProps.isCollapsed}
            cardsInsideCount={collapseProps.cardsInsideCount}
            isDraft={Boolean(draft)}
            showDraftDeleteButton={showDraftDeleteButton}
            onDraftDelete={handleDraftDelete}
            aiPanelSelectionInputName={`ai_product_select_${id}`}
            isAIPanelSelectionEnabled={isAIPanelSelectionEnabled}
            isAIPanelSelected={isAIPanelSelected}
            isAISessionPreviewSelected={isAISessionPreviewSelected}
            onToggleAIPanelSelection={(nextSelectedState) => (
              onToggleAIPanelCardSelection?.(id, nextSelectedState)
            )}
          />
        );
      case VALUE_CHAIN_UNIT_TYPES.SUPPLIER:
        return (
          <SupplierNode
            name={name}
            profitCenterIds={profitCenterIds}
            naceSection={naceSection}
            keySupplierTier1OfTotal={keySupplierTier1OfTotal}
            addressCountry={getCountryName(addressCountry || '')}
            totalCost={totalCost}
            isCollapsed={collapseProps.isCollapsed}
            cardsInsideCount={collapseProps.cardsInsideCount}
            isDraft={Boolean(draft)}
            showDraftDeleteButton={showDraftDeleteButton}
            onDraftDelete={handleDraftDelete}
            aiPanelSelectionInputName={`ai_supplier_select_${id}`}
            isAIPanelSelectionEnabled={isAIPanelSelectionEnabled}
            isAIPanelSelected={isAIPanelSelected}
            isAISessionPreviewSelected={isAISessionPreviewSelected}
            onToggleAIPanelSelection={(nextSelectedState) => (
              onToggleAIPanelCardSelection?.(id, nextSelectedState)
            )}
          />
        );
      default:
        return null;
    }
  };

  const handleCountryGroupClick = () => {
    if (isDraftReviewMode || isScenarioViewMode || !countryGroupId) {
      return;
    }

    onExpandCountryGroupStack?.(countryGroupId);
  };

  const getDragTargetClass = () => {
    if (!data.isDragTarget) {
      return '';
    }

    const dragType = data.dropZoneType;
    const nodeType = data.draggingNodeType;

    if (!dragType || !nodeType) {
      return styles.dragTarget;
    }

    if (dragType === 'reorder') {
      return '';
    }

    const typeClassMap: Record<string, string> = {
      supplier: dragType === 'nested' ? styles.dragTargetNestedSupplier : styles.dragTargetReorderSupplier,
      product: dragType === 'nested' ? styles.dragTargetNestedProduct : styles.dragTargetReorderProduct,
      'balance-sheet-asset': dragType === 'nested'
        ? styles.dragTargetNestedBalanceSheetAsset : styles.dragTargetReorderBalanceSheetAsset,
      'profit-center': dragType === 'nested'
        ? styles.dragTargetNestedProfitCenter : styles.dragTargetReorderProfitCenter,
      customer: dragType === 'nested' ? styles.dragTargetNestedCustomer : styles.dragTargetReorderCustomer,
    };

    return typeClassMap[nodeType] || styles.dragTarget;
  };

  const getContainerStyle = () => {
    const hasShift = Boolean(data.tempYOffset);
    const isReorderTarget = Boolean(data.isDragTarget && data.dropZoneType === 'reorder');

    if (hasShift || isReorderTarget) {
      return {
        ...(hasShift ? { transform: `translateY(${data.tempYOffset}px)` } : {}),
        ...(isReorderTarget ? { boxShadow: 'inset 0 6px 0 0 rgba(194, 126, 220, 0.45)' } : {}),
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      };
    }

    return undefined;
  };

  if (isCountryGroupLeaderCollapsed && firstRecord) {
    const countryName = getCountryName(firstRecord.addressCountry || '');

    return (
      <div
        className={clsx(styles.unitContainer, getDragTargetClass(), {
          [styles.draftReviewActive]: isDraftReviewMode && draft,
          [styles.draftReviewDimmed]: isDraftReviewMode && !draft,
          [styles.scenarioHighlighted]: isScenarioViewMode && isScenarioHighlighted,
          [styles.scenarioDimmed]: isScenarioViewMode && !isScenarioHighlighted,
          [styles.hiddenUnit]: data.isHiddenByParent || isHiddenByCountryGroup,
        })}
        style={getContainerStyle()}
      >
        <div className={styles.container}>
          <Handle
            type="source"
            position={Position.Right}
            className={clsx(styles.connectionHandle, styles.connectionHandlePhysical)}
            isConnectable={false}
          />
          <Handle
            type="target"
            position={Position.Left}
            className={clsx(styles.connectionHandle, styles.connectionHandlePhysical)}
            isConnectable={false}
          />
          <div
            className={clsx(styles.connectionHandleDot, styles.connectionHandleDotRight)}
            aria-hidden
          />
          <div
            className={clsx(styles.connectionHandleDot, styles.connectionHandleDotLeft)}
            aria-hidden
          />
          <div className={styles.countryGroupStack}>
            <div
              className={clsx(styles.countryGroupStackCard, {
                [styles.aiMetadataHighlighted]: isAIHighlightedUnit,
              })}
              data-type={type}
            >
              <button
                type="button"
                className={styles.countryGroupStackHeader}
                onClick={handleCountryGroupClick}
                aria-label={`${countryName}, ${cardsInsideCount} cards`}
                tabIndex={0}
              >
                <EarthIcon className={styles.countryGroupStackIcon} aria-hidden />
                <span className={styles.countryGroupStackTitle}>{countryName.toUpperCase()}</span>
                <span className={styles.countryGroupStackCount}>{cardsInsideCount}</span>
              </button>
            </div>
            <div className={styles.countryGroupStackLayers} aria-hidden>
              <span className={styles.countryGroupStackLayer} />
              <span className={styles.countryGroupStackLayer} />
              <span className={styles.countryGroupStackLayer} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.unitContainer, getDragTargetClass(), {
        [styles.draftReviewActive]: isDraftReviewMode && draft,
        [styles.draftReviewDimmed]: isDraftReviewMode && !draft,
        [styles.scenarioHighlighted]: isScenarioViewMode && isScenarioHighlighted,
        [styles.scenarioDimmed]: isScenarioViewMode && !isScenarioHighlighted,
        [styles.hiddenUnit]: data.isHiddenByParent || isHiddenByCountryGroup,
      })}
      style={getContainerStyle()}
    >
      {(records && records.length > 0 ? records : [null]).map((record, index) => {
        if (type === VALUE_CHAIN_UNIT_TYPES.PROFIT_CENTER) {
          return null;
        }

        const isGroupLeaderRecordCollapsed = isCountryGroupLeaderCollapsed;
        const recordName = isGroupLeaderRecordCollapsed
          ? getCountryName(record?.addressCountry || '')
          : (record?.name || getUnitDisplayName(data));
        const displayRecord: TDisplayRecord = {
          ...record,
          name: recordName,
          profitCenterIds: record?.profitCenterIds ?? [],
          uniqueIdentifier: record?.uniqueIdentifier ?? '',
        };
        const isAIMetadataHighlighted = hasAIMetadata(data.aiMetadata) || hasAIMetadata(record?.aiMetadata);

        return (
          <CustomUnitContainer
            key={`records_${record?.id ?? `unit_${data.id}_${index}`}`}
            isChild={!!parentId}
            type={type}
            areHandlesHidden={isCountryGroupLeaderCollapsed}
            businessUnitType={unitBusinessUnitType}
            estimated={unitEstimated}
            childrenCount={childrenCount}
            totalChildrenHeight={effectiveTotalChildrenHeight}
            depth={depth}
            parentWidth={parentUnitWidth}
            isNestedDropTarget={Boolean(data.isDragTarget && data.dropZoneType === 'nested')}
            nestedPlaceholderHeight={data.dropZoneType === 'reorder' ? undefined : data.tempHeight}
            isCollapsed={Boolean(isCollapsed)}
            isAIMetadataHighlighted={isAIMetadataHighlighted}
            showMonitoringRiskBadge={Boolean(
              showMonitoringRiskBadge
              && !isDraftReviewMode,
            )}
          >
            {renderNodeContent(
              displayRecord,
              {
                isCollapsed: isGroupLeaderRecordCollapsed ? true : Boolean(isCollapsed),
                cardsInsideCount,
              },
            )}
          </CustomUnitContainer>
        );
      })}
    </div>
  );
}

export default memo(CustomUnitNode, areCustomUnitNodePropsEqual);
